import { useMemo, useRef, useState } from 'react';
import { AttachmentGallery } from '../components/AttachmentGallery';
import { Checkbox } from '../components/Checkbox';
import { Lightbox } from '../components/Lightbox';
import { MarkdownEditor } from '../components/MarkdownEditor';
import { NoteRow } from '../components/NoteRow';
import { QuickAdd, type Draft } from '../components/QuickAdd';
import { SwipeRow } from '../components/SwipeRow';
import { TaskRow, Marked } from '../components/TaskRow';
import { DatePicker } from '../components/DatePicker';
import { MobileSheet, SheetField, SheetRow } from '../components/MobileSheet';
import {
  createItem,
  findOrCreateGlobalTag,
  findOrCreateList,
  moveToList,
  restore,
  softDelete,
  toggleDone,
  updateItem,
} from '../db/dexie';
import {
  INBOX_LIST_ID,
  TRASH_RETENTION_DAYS,
  emptyView,
  type Item,
  type Kind,
  type List,
  type Priority,
} from '../db/types';
import { colorOf } from '../lib/colors';
import { exportJSON } from '../lib/export';
import { purgeItem } from '../sync/purge';
import { isConfigured, supabase } from '../sync/supabase';
import {
  useAttachmentCounts,
  useAttachments,
  useCatalog,
  useItems,
  useSearchIndex,
} from '../hooks/useData';
import { useTypedText } from '../hooks/useTypedText';
import type { Catalog } from '../lib/views';
import { syncLabel, type SyncStatus } from '../hooks/useSync';
import { useUndo } from '../hooks/useUndo';
import {
  dayKey,
  formatDayMonth,
  formatDueShort,
  formatModified,
  formatWeekday,
  monthName,
  parseMonthKey,
  shiftMonthKey,
  thisMonthKey,
  todayISO,
  tomorrowISO,
} from '../lib/dates';
import { excerpt, search } from '../lib/search';
import { calendarAnchor, calendarDays, sectionsFor, type CalendarDay } from '../lib/views';
import './MobileShell.css';

type Tab = 'today' | 'calendar' | 'notes' | 'search';
type Screen =
  | { name: 'list' }
  | { name: 'item'; id: string }
  | { name: 'capture' }
  | { name: 'trash' }
  | { name: 'settings' };

/**
 * A capture-and-review client, not the desktop app at a narrow width.
 * List management, the tag tree, drag reordering, start dates, bulk operations
 * and export are simply absent — not compressed into a smaller space.
 */
export function MobileShell({ sync }: { sync: SyncStatus }) {
  const items = useItems();
  const catalog = useCatalog();
  const lists = catalog.lists;
  const imageCounts = useAttachmentCounts();
  const index = useSearchIndex(items);
  const { offer } = useUndo();

  const [tab, setTab] = useState<Tab>('today');
  const [screen, setScreen] = useState<Screen>({ name: 'list' });
  const [query, setQuery] = useState('');
  const [scope, setScope] = useState<'all' | 'todo' | 'note'>('all');
  const [menuOpen, setMenuOpen] = useState(false);

  // Calendar state lives here, not in the tab, so the month you were reading
  // survives a trip into a note and back — and so the FAB knows which day a
  // new note should land on.
  const [month, setMonth] = useState(thisMonthKey);
  const [day, setDay] = useState(() => dayKey(new Date()));

  const days = useMemo(() => calendarDays(month, items), [month, items]);

  /**
   * Paging the month has to move the selection too, or the agenda underneath
   * would go on showing a day that is no longer on screen. Landing on today
   * when you page back to the current month is the one case worth special-
   * casing: it is where you were before you went wandering.
   */
  const goMonth = (by: number) => {
    const next = shiftMonthKey(month, by);
    setMonth(next);
    setDay(next === thisMonthKey() ? dayKey(new Date()) : `${next}-01`);
  };

  const open = useMemo(
    () => (screen.name === 'item' ? items.find((i) => i.id === screen.id && !i.deleted) ?? null : null),
    [screen, items],
  );

  const remove = (item: Item) => {
    void softDelete(item.id);
    if (screen.name === 'item' && screen.id === item.id) setScreen({ name: 'list' });
    offer('Moved to Trash', () => void restore(item.id));
  };

  const complete = (item: Item) => {
    void toggleDone(item);
    if (!item.done) offer('Task completed', () => void toggleDone({ ...item, done: true }));
  };

  const selected = days.find((d) => d.key === day) ?? null;

  if (screen.name === 'capture') {
    return (
      <div className="shell shell-mobile">
        <Capture
          lists={lists}
          catalog={catalog}
          // Capturing from the calendar dates the item to the day you were
          // looking at. Typing "friday" still wins — the parsed date is the
          // one you asked for out loud.
          defaultDue={tab === 'calendar' ? (selected?.date ?? null) : null}
          onCancel={() => setScreen({ name: 'list' })}
          onSave={async (kind, title, patch) => {
            const item = await createItem({ kind, title, ...patch });
            setScreen(kind === 'note' ? { name: 'item', id: item.id } : { name: 'list' });
          }}
        />
      </div>
    );
  }

  if (screen.name === 'trash') {
    return (
      <div className="shell shell-mobile">
        <TrashScreen
          items={items}
          imageCounts={imageCounts}
          onBack={() => setScreen({ name: 'list' })}
        />
      </div>
    );
  }

  if (screen.name === 'settings') {
    return (
      <div className="shell shell-mobile">
        <SettingsScreen
          catalog={catalog}
          sync={sync}
          onBack={() => setScreen({ name: 'list' })}
          onTrash={() => setScreen({ name: 'trash' })}
          trashCount={items.filter((i) => i.deleted).length}
        />
      </div>
    );
  }

  if (open) {
    return (
      <div className="shell shell-mobile">
        <MobileEditor
          item={open}
          catalog={catalog}
          onBack={() => setScreen({ name: 'list' })}
          onDelete={() => remove(open)}
        />
      </div>
    );
  }

  return (
    <div className="shell shell-mobile">
      {/* A failure that only exists in a tooltip is a failure you never read —
          and on a phone there is no tooltip at all. The server's own words,
          above everything, until the next cycle succeeds. */}
      {sync.error && <p className="msync__error">{sync.error}</p>}

      {tab === 'today' && (
        <TodayTab
          items={items}
          catalog={catalog}
          imageCounts={imageCounts}
          sync={sync}
          onMenu={() => setMenuOpen(true)}
          onOpen={(item) => setScreen({ name: 'item', id: item.id })}
          onComplete={complete}
          onDelete={remove}
        />
      )}

      {tab === 'calendar' && (
        <CalendarTab
          month={month}
          days={days}
          selected={selected}
          catalog={catalog}
          imageCounts={imageCounts}
          sync={sync}
          onMenu={() => setMenuOpen(true)}
          onMonth={goMonth}
          onToday={() => {
            setMonth(thisMonthKey());
            setDay(dayKey(new Date()));
          }}
          onDay={setDay}
          onOpen={(item) => setScreen({ name: 'item', id: item.id })}
          onComplete={complete}
          onDelete={remove}
        />
      )}

      {tab === 'notes' && (
        <NotesTab
          items={items}
          imageCounts={imageCounts}
          sync={sync}
          onMenu={() => setMenuOpen(true)}
          onOpen={(item) => setScreen({ name: 'item', id: item.id })}
          onDelete={remove}
        />
      )}

      {tab === 'search' && (
        <SearchTab
          items={items}
          catalog={catalog}
          index={index}
          query={query}
          scope={scope}
          onQuery={setQuery}
          onScope={setScope}
          onOpen={(item) => setScreen({ name: 'item', id: item.id })}
        />
      )}

      <button
        type="button"
        className="fab"
        aria-label="New item"
        onClick={() => setScreen({ name: 'capture' })}
      >
        +
      </button>

      {menuOpen && (
        <MobileSheet title="More" onClose={() => setMenuOpen(false)}>
          <SheetRow
            label="Settings"
            onClick={() => {
              setMenuOpen(false);
              setScreen({ name: 'settings' });
            }}
          />
          <SheetRow
            label="Trash"
            detail={`${items.filter((i) => i.deleted).length}`}
            onClick={() => {
              setMenuOpen(false);
              setScreen({ name: 'trash' });
            }}
          />
          <SheetRow
            label="Export everything"
            detail="JSON"
            onClick={() => {
              setMenuOpen(false);
              void exportJSON();
            }}
          />
          {isConfigured && (
            <SheetRow
              label="Sign out"
              tone="danger"
              onClick={() => {
                setMenuOpen(false);
                void supabase?.auth.signOut();
              }}
            />
          )}
        </MobileSheet>
      )}

      <nav className="tabs" aria-label="Sections">
        {(
          [
            ['today', 'Today'],
            ['calendar', 'Calendar'],
            ['notes', 'Notes'],
            ['search', 'Search'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={`tabs__tab${tab === id ? ' is-active' : ''}`}
            aria-current={tab === id ? 'page' : undefined}
            onClick={() => setTab(id)}
          >
            <span className={`tabs__glyph tabs__glyph--${id}`} aria-hidden="true">
              {id === 'search' ? '⌕' : ''}
            </span>
            <span className="tabs__label">{label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}

/* ── Sync ──────────────────────────────────────────────────── */

/**
 * Status and manual trigger in one control, sitting at the right of every list
 * header — the mobile counterpart of the sidebar's status button.
 *
 * The label is always shown rather than hidden behind a healthy-state dot: on
 * a phone there is no hover, no title attribute and no menu to go looking in,
 * so a control that says nothing until something is wrong is a control nobody
 * knows exists. Syncing is automatic anyway; this is for the moment you want
 * to *know*, standing somewhere with one bar of signal.
 */
function SyncButton({ sync }: { sync: SyncStatus }) {
  const label = syncLabel(sync);
  return (
    <button
      type="button"
      className={`msync msync--${sync.phase}`}
      aria-label={`Sync status: ${label}. Tap to sync now.`}
      aria-live="polite"
      onClick={sync.syncNow}
    >
      <span className="msync__dot" aria-hidden="true" />
      <span className="msync__label">{label}</span>
    </button>
  );
}

/**
 * Everything that is not one of the four tabs. A phone has no sidebar to park
 * Trash and Settings in, and burying them behind a long-press would make them
 * findable only by someone who already knew they were there.
 */
function MoreButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className="mhead__icon" aria-label="More" onClick={onClick}>
      ⋯
    </button>
  );
}

/* ── Today ─────────────────────────────────────────────────── */

function TodayTab({
  items,
  catalog,
  imageCounts,
  sync,
  onMenu,
  onOpen,
  onComplete,
  onDelete,
}: {
  items: Item[];
  catalog: Catalog;
  imageCounts: Record<string, number>;
  sync: SyncStatus;
  onMenu: () => void;
  onOpen: (item: Item) => void;
  onComplete: (item: Item) => void;
  onDelete: (item: Item) => void;
}) {
  const sections = useMemo(() => sectionsFor(emptyView({ type: 'today' }), items), [items]);

  /*
   * Open tasks carrying no date at all.
   *
   * Today is scoped to dates, the calendar can only file something that has
   * one, and the Notes tab is notes. That left an undated task — which is what
   * the capture screen produces by default — visible nowhere on the phone,
   * despite saving and syncing perfectly well. Desktop never shows the hole
   * because Inbox and All Notes catch them; mobile has neither, so they belong
   * here, below the dated work rather than mixed into it.
   *
   * Deliberately not fixed by dating captures automatically: that would make an
   * undated task impossible to create on a phone, and the problem was only ever
   * that they could not be seen.
   */
  const undated = useMemo(
    () =>
      items
        .filter((i) => !i.deleted && i.kind === 'todo' && !i.done && !i.due_at && !i.start_at)
        .sort((a, b) => b.created_at.localeCompare(a.created_at)),
    [items],
  );

  return (
    <>
      <header className="mhead">
        <h1 className="mhead__title">Today</h1>
        <SyncButton sync={sync} />
        <MoreButton onClick={onMenu} />
      </header>
      <div className="mscroll">
        {sections.length === 0 && undated.length === 0 ? (
          <div className="empty">
            <div className="empty__title">Nothing due today</div>
            <div className="empty__hint">Anything starting later is waiting until its day.</div>
          </div>
        ) : (
          sections.map((section) => (
            <div key={section.key}>
              <div
                className={`section-head${section.tone === 'overdue' ? ' section-head--overdue' : ''} mhead__section`}
              >
                <span>{section.label}</span>
                <span className="section-head__count">{section.items.length}</span>
              </div>
              {section.items.map((item) =>
                item.kind === 'note' ? (
                  <SwipeRow key={item.id} onDelete={() => onDelete(item)}>
                    <NoteRow item={item} images={imageCounts[item.id] ?? 0} onOpen={() => onOpen(item)} />
                  </SwipeRow>
                ) : (
                  <SwipeRow
                    key={item.id}
                    onComplete={() => onComplete(item)}
                    onDelete={() => onDelete(item)}
                  >
                    <TaskRow
                      item={item}
                      catalog={catalog}
                      dateStyle="time"
                      onOpen={() => onOpen(item)}
                      onToggle={() => onComplete(item)}
                    />
                  </SwipeRow>
                ),
              )}
            </div>
          ))
        )}

        {undated.length > 0 && (
          <div>
            <div className="section-head mhead__section">
              <span>No date</span>
              <span className="section-head__count">{undated.length}</span>
            </div>
            {undated.map((item) => (
              <SwipeRow
                key={item.id}
                onComplete={() => onComplete(item)}
                onDelete={() => onDelete(item)}
              >
                <TaskRow
                  item={item}
                  catalog={catalog}
                  onOpen={() => onOpen(item)}
                  onToggle={() => onComplete(item)}
                />
              </SwipeRow>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

/* ── Calendar ──────────────────────────────────────────────── */

const DOW = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

/** Far enough that a horizontal drag cannot be mistaken for a vertical scroll. */
const SWIPE = 56;

/**
 * A month you can read with a thumb.
 *
 * Desktop puts item chips inside each cell; at 49px across there is no room
 * for one, so the same idea is carried by the cell itself — a day holding
 * something sits on `bg-surface`, an empty day on nothing. The month still
 * reads as a topography of what you wrote, and the agenda underneath answers
 * *what* for the one day you have chosen.
 */
function CalendarTab({
  month,
  days,
  selected,
  catalog,
  imageCounts,
  sync,
  onMenu,
  onMonth,
  onToday,
  onDay,
  onOpen,
  onComplete,
  onDelete,
}: {
  month: string;
  days: CalendarDay[];
  selected: CalendarDay | null;
  catalog: Catalog;
  imageCounts: Record<string, number>;
  sync: SyncStatus;
  onMenu: () => void;
  onMonth: (by: number) => void;
  onToday: () => void;
  onDay: (key: string) => void;
  onOpen: (item: Item) => void;
  onComplete: (item: Item) => void;
  onDelete: (item: Item) => void;
}) {
  const swipe = useRef<{ x: number; y: number } | null>(null);
  const first = parseMonthKey(month);
  const agenda = selected?.items ?? [];

  return (
    <>
      <header className="mhead">
        <h1 className="mhead__title">Calendar</h1>
        <SyncButton sync={sync} />
        <MoreButton onClick={onMenu} />
      </header>

      {/*
        The month name sits with the controls that change it rather than in the
        tab title. That is where a reader looks for it, and it leaves the header
        the room it needs for sync and the menu — which is what forced the title
        down a type size when the two were sharing a line.
      */}
      <div className="mcal__monthBar">
        <button
          type="button"
          className="mhead__icon"
          aria-label="Previous month"
          onClick={() => onMonth(-1)}
        >
          ‹
        </button>
        <span className="mcal__month" aria-live="polite">
          {monthName(first)} <span className="mcal__year">{first.getFullYear()}</span>
        </span>
        <button
          type="button"
          className="mhead__icon"
          aria-label="Next month"
          onClick={() => onMonth(1)}
        >
          ›
        </button>
        <button
          type="button"
          className="mcal__todayBtn"
          disabled={month === thisMonthKey()}
          onClick={onToday}
        >
          Today
        </button>
      </div>

      {/*
        Month and agenda share one scroll surface. A pinned grid costs 298px,
        which on a 640px phone leaves under one row of agenda visible — so the
        month scrolls away instead, and the day header sticks so you never lose
        track of which day you are reading.
      */}
      <div className="mscroll mscroll--cal">
      <div className="mcal__dow" aria-hidden="true">
        {DOW.map((d, i) => (
          <span key={i}>{d}</span>
        ))}
      </div>

      {/* Swiping the month matches the swipe grammar the rows already use.
          The buttons above stay, because a gesture with no visible control is
          a feature only its author knows about. */}
      <div
        className="mcal__grid"
        role="grid"
        aria-label="Days"
        onTouchStart={(e) => {
          const t = e.touches[0]!;
          swipe.current = { x: t.clientX, y: t.clientY };
        }}
        onTouchEnd={(e) => {
          const start = swipe.current;
          swipe.current = null;
          if (!start) return;
          const t = e.changedTouches[0]!;
          const dx = t.clientX - start.x;
          // Vertical intent wins: the page scrolls under this grid.
          if (Math.abs(dx) < SWIPE || Math.abs(t.clientY - start.y) > Math.abs(dx)) return;
          onMonth(dx < 0 ? 1 : -1);
        }}
      >
        {days.map((d) => (
          <button
            key={d.key}
            type="button"
            role="gridcell"
            aria-pressed={d.key === selected?.key}
            className={[
              'mcal__cell',
              d.items.length > 0 && 'has-items',
              !d.inMonth && 'is-outside',
              d.isToday && 'is-today',
              d.key === selected?.key && 'is-selected',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => onDay(d.key)}
          >
            <span className="mcal__num">{d.date.getDate()}</span>
          </button>
        ))}
      </div>

      {selected && (
        <div className="mcal__agendaHead">
          <span className="mcal__agendaDay">
            {formatWeekday(selected.date.toISOString())}{' '}
            <span className="muted">{formatDayMonth(selected.date.toISOString())}</span>
          </span>
          <span className="section-head__count">{agenda.length}</span>
        </div>
      )}

      <div className="mcal__agenda">
        {agenda.length === 0 ? (
          <div className="empty">
            <div className="empty__title">Nothing on this day</div>
            <div className="empty__hint">Tap + to write a note dated here.</div>
          </div>
        ) : (
          agenda.map((item) =>
            item.kind === 'note' ? (
              <SwipeRow key={item.id} onDelete={() => onDelete(item)}>
                <NoteRow item={item} images={imageCounts[item.id] ?? 0} onOpen={() => onOpen(item)} />
              </SwipeRow>
            ) : (
              <SwipeRow key={item.id} onComplete={() => onComplete(item)} onDelete={() => onDelete(item)}>
                <TaskRow
                  item={item}
                  catalog={catalog}
                  dateStyle="time"
                  onOpen={() => onOpen(item)}
                  onToggle={() => onComplete(item)}
                />
              </SwipeRow>
            ),
          )
        )}
      </div>
      </div>
    </>
  );
}

/* ── Notes ─────────────────────────────────────────────────── */

function NotesTab({
  items,
  imageCounts,
  sync,
  onMenu,
  onOpen,
  onDelete,
}: {
  items: Item[];
  imageCounts: Record<string, number>;
  sync: SyncStatus;
  onMenu: () => void;
  onOpen: (item: Item) => void;
  onDelete: (item: Item) => void;
}) {
  const notes = useMemo(
    () =>
      items
        .filter((i) => i.kind === 'note' && !i.deleted)
        .sort((a, b) => b.updated_at.localeCompare(a.updated_at)),
    [items],
  );

  return (
    <>
      <header className="mhead">
        <h1 className="mhead__title">Notes</h1>
        <span className="mhead__detail">{notes.length}</span>
        <SyncButton sync={sync} />
        <MoreButton onClick={onMenu} />
      </header>
      <div className="mscroll">
        {notes.length === 0 ? (
          <div className="empty">
            <div className="empty__title">No notes yet</div>
            <div className="empty__hint">Tap + to write one.</div>
          </div>
        ) : (
          notes.map((note) => (
            <SwipeRow key={note.id} onDelete={() => onDelete(note)}>
              <NoteRow item={note} bare images={imageCounts[note.id] ?? 0} onOpen={() => onOpen(note)} />
            </SwipeRow>
          ))
        )}
      </div>
    </>
  );
}

/* ── Search ────────────────────────────────────────────────── */

function SearchTab({
  items,
  catalog,
  index,
  query,
  scope,
  onQuery,
  onScope,
  onOpen,
}: {
  items: Item[];
  catalog: Catalog;
  index: ReturnType<typeof useSearchIndex>;
  query: string;
  scope: 'all' | 'todo' | 'note';
  onQuery: (q: string) => void;
  onScope: (s: 'all' | 'todo' | 'note') => void;
  onOpen: (item: Item) => void;
}) {
  const live = useMemo(() => items.filter((i) => !i.deleted), [items]);
  // Only touch the index once there is something to look for; building it is a
  // pass over every note, and this screen renders long before anyone types.
  const hits = useMemo(
    () => (query.trim() ? search(index(), live, query) : []),
    [index, live, query],
  );
  const tasks = hits.filter((h) => h.item.kind === 'todo');
  const notes = hits.filter((h) => h.item.kind === 'note');
  const showTasks = scope !== 'note';
  const showNotes = scope !== 'todo';

  return (
    <>
      <div className="msearch">
        <div className="msearch__field">
          <span className="muted" aria-hidden="true">
            ⌕
          </span>
          <input
            className="msearch__input"
            value={query}
            autoFocus
            placeholder="Search everything"
            aria-label="Search"
            onChange={(e) => onQuery(e.target.value)}
          />
          {query && (
            <button type="button" className="msearch__clear" aria-label="Clear" onClick={() => onQuery('')}>
              ✕
            </button>
          )}
        </div>
        <div className="msearch__scopes">
          {(
            [
              ['all', 'All'],
              ['todo', 'Tasks'],
              ['note', 'Notes'],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              className={`msearch__scope${scope === id ? ' is-active' : ''}`}
              onClick={() => onScope(id)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="mscroll">
        {query && hits.length === 0 && (
          <div className="empty">
            <div className="empty__title">No matches for “{query}”</div>
            <div className="empty__hint">Search covers titles, bodies and tags.</div>
          </div>
        )}
        {!query && (
          <div className="empty">
            <div className="empty__title">Search everything</div>
            <div className="empty__hint">Titles, bodies and tags — fuzzy, and entirely offline.</div>
          </div>
        )}

        {showTasks && tasks.length > 0 && (
          <>
            <div className="section-head mhead__section">
              <span>Tasks</span>
              <span className="section-head__count">{tasks.length}</span>
            </div>
            {tasks.map((hit) => (
              <TaskRow
                key={hit.item.id}
                item={hit.item}
                catalog={catalog}
                terms={hit.terms}
                onOpen={() => onOpen(hit.item)}
              />
            ))}
          </>
        )}

        {showNotes && notes.length > 0 && (
          <>
            <div className="section-head mhead__section">
              <span>Notes</span>
              <span className="section-head__count">{notes.length}</span>
            </div>
            {notes.map((hit) => (
              <div className="row row--tall" key={hit.item.id} onClick={() => onOpen(hit.item)}>
                <div className="row__body">
                  <div className="row__title">
                    <Marked text={hit.item.title} terms={hit.terms} />
                  </div>
                  <div className="row__meta">
                    <Marked text={excerpt(hit.item.body, hit.terms)} terms={hit.terms} />
                  </div>
                </div>
                <span className="row__count">{formatModified(hit.item.updated_at)}</span>
              </div>
            ))}
          </>
        )}
      </div>
    </>
  );
}

/* ── Trash ───────────────────────────────────────────── */

/**
 * Deleted items, restorable until the retention sweep takes them.
 *
 * Mobile had no route to this at all, which meant the undo toast was the only
 * recovery there was — and it lasts five seconds. Anything deleted on a phone
 * and regretted a minute later was gone until you reached a desktop.
 */
function TrashScreen({
  items,
  imageCounts,
  onBack,
}: {
  items: Item[];
  imageCounts: Record<string, number>;
  onBack: () => void;
}) {
  const [purging, setPurging] = useState<Item | null>(null);
  const deleted = useMemo(
    () => items.filter((i) => i.deleted).sort((a, b) => b.updated_at.localeCompare(a.updated_at)),
    [items],
  );

  return (
    <>
      <header className="mhead mhead--editor">
        <button type="button" className="mhead__back" onClick={onBack}>
          ‹ Back
        </button>
        <div className="spacer" />
        <span className="mhead__detail">Trash · {deleted.length}</span>
      </header>

      <div className="mscroll">
        <p className="mnote">
          Deleted items stay here for {TRASH_RETENTION_DAYS} days, then go for good.
        </p>

        {deleted.length === 0 ? (
          <div className="empty">
            <div className="empty__title">Trash is empty</div>
            <div className="empty__hint">Anything you delete lands here first.</div>
          </div>
        ) : (
          deleted.map((item) => (
            <div className="row row--tall mtrash__row" key={item.id}>
              <div className="row__body">
                <div className="row__title">{item.title || 'Untitled'}</div>
                <div className="row__meta">
                  {item.kind === 'todo' ? 'Task' : 'Note'} · deleted{' '}
                  {formatModified(item.deleted_at ?? item.updated_at)}
                  {imageCounts[item.id] ? ` · ${imageCounts[item.id]} img` : ''}
                </div>
              </div>
              <button type="button" className="mtrash__action" onClick={() => void restore(item.id)}>
                Restore
              </button>
              <button
                type="button"
                className="mtrash__action mtrash__action--danger"
                aria-label={`Delete ${item.title || 'Untitled'} permanently`}
                onClick={() => setPurging(item)}
              >
                ⌫
              </button>
            </div>
          ))
        )}
      </div>

      {/* There is no undo for a purge, so this stops to ask — the same rule the
          desktop Trash follows. */}
      {purging && (
        <MobileSheet
          title="Delete permanently?"
          hint="This cannot be undone, on this device or any other."
          onClose={() => setPurging(null)}
        >
          <p className="sheet__prose">
            <strong>{purging.title || 'Untitled'}</strong> and any images attached to it will be
            destroyed everywhere, instead of waiting out the {TRASH_RETENTION_DAYS}-day retention.
          </p>
          <SheetRow
            label="Delete forever"
            tone="danger"
            onClick={() => {
              const item = purging;
              setPurging(null);
              void purgeItem(item.id);
            }}
          />
        </MobileSheet>
      )}
    </>
  );
}

/* ── Settings ────────────────────────────────────────── */

function SettingsScreen({
  catalog,
  sync,
  trashCount,
  onBack,
  onTrash,
}: {
  catalog: Catalog;
  sync: SyncStatus;
  trashCount: number;
  onBack: () => void;
  onTrash: () => void;
}) {
  return (
    <>
      <header className="mhead mhead--editor">
        <button type="button" className="mhead__back" onClick={onBack}>
          ‹ Back
        </button>
        <div className="spacer" />
        <span className="mhead__detail">Settings</span>
      </header>

      <div className="mscroll">
        <div className="section-head mhead__section">
          <span>Organisation</span>
        </div>
        <MSettingRow label="Lists" value={`${catalog.lists.length}`} />
        <MSettingRow label="List tags" value={`${catalog.listTags.length}`} />
        <MSettingRow label="Global tags" value={`${catalog.globalTags.length}`} />
        <MSettingRow label="Trash" value={`${trashCount}`} onClick={onTrash} />

        <div className="section-head mhead__section">
          <span>Data</span>
        </div>
        <MSettingRow label="Export" value="Everything as JSON" onClick={() => void exportJSON()} />
        <MSettingRow
          label="Sync"
          value={isConfigured ? syncLabel(sync) : 'Local only'}
          onClick={sync.syncNow}
        />
        {isConfigured && (
          <MSettingRow
            label="Sign out"
            value="This device"
            danger
            onClick={() => void supabase?.auth.signOut()}
          />
        )}

        <div className="section-head mhead__section">
          <span>About</span>
        </div>
        <MSettingRow label="Appearance" value="Dark only" />
        {/* Creating and renaming lists stays desktop work, as the brief has it.
            This screen reports what exists rather than pretending to manage it. */}
        <p className="mnote">Lists and tags are created and renamed on the desktop app.</p>
      </div>
    </>
  );
}

function MSettingRow({
  label,
  value,
  danger,
  onClick,
}: {
  label: string;
  value: string;
  danger?: boolean;
  onClick?: () => void;
}) {
  const className = `mset${danger ? ' mset--danger' : ''}`;
  const inner = (
    <>
      <span className="mset__label">{label}</span>
      <span className="mset__value">{value}</span>
    </>
  );
  return onClick ? (
    <button type="button" className={className} onClick={onClick}>
      {inner}
    </button>
  ) : (
    <div className={className}>{inner}</div>
  );
}

/* ── Capture ─────────────────────────────────────────── */

/**
 * Fields set by tapping rather than by typing.
 *
 * Sparse on purpose: a key is present only once you have touched that control,
 * which is what lets the merge at save time be a plain spread. Typed text
 * supplies everything you have not chosen explicitly, and an explicit choice
 * always beats the parser's guess.
 */
interface Extras {
  due_at?: string | null;
  priority?: Priority;
  list_id?: string;
  global_tag_ids?: string[];
}

function Capture({
  lists,
  catalog,
  defaultDue,
  onCancel,
  onSave,
}: {
  lists: List[];
  catalog: Catalog;
  /** The day the capture was started from, or null when it carries no date. */
  defaultDue: Date | null;
  onCancel: () => void;
  onSave: (kind: Kind, title: string, patch: Partial<Item>) => void | Promise<void>;
}) {
  const [kind, setKind] = useState<Kind>('todo');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [extras, setExtras] = useState<Extras>({});
  const [sheet, setSheet] = useState<'date' | 'list' | 'tags' | null>(null);

  /* Speed here matters more than anything else in the app — if capture takes
     more than about three seconds it will not get used. Enter is the fast path;
     Save exists for the times a thumb is already near the top of the screen. */
  const commit = async (d: Draft | null) => {
    if (!d) return;

    // An unmatched `#name` brings the list into being — the same rule as
    // desktop, since capture is the one place mobile does real input.
    const list_id =
      extras.list_id ??
      (d.parsed.listName
        ? (await findOrCreateList(d.parsed.listName)).id
        : (d.parsed.list_id ?? INBOX_LIST_ID));

    // Tapped and typed tags add up rather than compete — there is no sense in
    // which picking one from the sheet means "not the one I typed".
    const global_tag_ids = [...(extras.global_tag_ids ?? [])];
    // `@name` is a global tag: on a phone you are capturing before you know
    // which list it belongs to, so a list-scoped tag could not be resolved yet.
    for (const name of d.parsed.tags) {
      const tag = await findOrCreateGlobalTag(name);
      if (!global_tag_ids.includes(tag.id)) global_tag_ids.push(tag.id);
    }

    const dated = new Date(defaultDue ?? 0);
    dated.setHours(0, 0, 0, 0);

    await onSave(kind, d.parsed.title || d.raw, {
      due_at:
        extras.due_at !== undefined
          ? extras.due_at
          : (d.parsed.due_at ?? (defaultDue ? dated.toISOString() : null)),
      priority: extras.priority ?? d.parsed.priority,
      list_id,
      global_tag_ids,
    });
  };

  const pickedList = lists.find((l) => l.id === extras.list_id);
  const tagCount = extras.global_tag_ids?.length ?? 0;
  const isDue = (iso: string) => extras.due_at === iso;
  const otherDate = Boolean(extras.due_at) && !isDue(todayISO()) && !isDue(tomorrowISO());

  return (
    <>
      <header className="capture__bar">
        <button type="button" className="capture__cancel" onClick={onCancel}>
          Cancel
        </button>
        <span className="capture__label">NEW</span>
        <button type="button" className="capture__save" disabled={!draft} onClick={() => void commit(draft)}>
          Save
        </button>
      </header>

      <div className="capture__body">
        <QuickAdd
          lists={lists}
          autoFocus
          placeholder={kind === 'note' ? 'Write a note…' : 'What needs doing?'}
          onDraft={setDraft}
          onSubmit={(parsed, raw) => void commit({ raw, parsed })}
        />
        {/* The syntax was the only way to set any of this and nothing on screen
            said so. Showing the tokens beats describing them. */}
        <p className="capture__hint">
          Type <span className="mono">tomorrow</span> <span className="mono">p1</span>{' '}
          <span className="mono">#list</span> <span className="mono">@tag</span> — or tap below.
        </p>
      </div>

      <div className="spacer" />

      {/*
        Date and priority resolve here without opening anything, so the keyboard
        never goes away for the two fields set most often. List and tags are
        unbounded, so they get a sheet — by which point the typing is usually
        finished anyway.
      */}
      <div className="capture__chips">
        <button
          type="button"
          className={`opt${isDue(todayISO()) ? ' is-on' : ''}`}
          onClick={() => setExtras((e) => ({ ...e, due_at: isDue(todayISO()) ? null : todayISO() }))}
        >
          Today
        </button>
        <button
          type="button"
          className={`opt${isDue(tomorrowISO()) ? ' is-on' : ''}`}
          onClick={() =>
            setExtras((e) => ({ ...e, due_at: isDue(tomorrowISO()) ? null : tomorrowISO() }))
          }
        >
          Tomorrow
        </button>
        {/* List and tags sit ahead of the rarer controls, because eight chips
            do not fit on a 360px phone and these are the two that had no way
            in at all before. The last chip stays half-visible on purpose —
            a clipped edge is what tells you the row scrolls. */}
        <button
          type="button"
          className={`opt${pickedList ? ' is-on' : ''}`}
          onClick={() => setSheet('list')}
        >
          {pickedList?.name ?? 'List…'}
        </button>
        <button
          type="button"
          className={`opt${tagCount > 0 ? ' is-on' : ''}`}
          onClick={() => setSheet('tags')}
        >
          {tagCount > 0 ? `${tagCount} tag${tagCount === 1 ? '' : 's'}` : 'Tags…'}
        </button>

        <button
          type="button"
          className={`opt${otherDate ? ' is-on' : ''}`}
          onClick={() => setSheet('date')}
        >
          {otherDate ? formatDueShort(extras.due_at!) : 'Date…'}
        </button>

        {/* Urgent only. The brief keeps priority editing on mobile to a single
            toggle, and `p2` / `p3` still work typed — spending three of the six
            visible slots on priority would push tags off the screen again. */}
        {kind === 'todo' && (
          <button
            type="button"
            className={`opt${extras.priority === 1 ? ' is-on' : ''}`}
            onClick={() => setExtras((e) => ({ ...e, priority: e.priority === 1 ? 4 : 1 }))}
          >
            <span className="pri-dot pri-dot--1" aria-hidden="true" />
            Urgent
          </button>
        )}
      </div>

      <div className="capture__tools">
        {(
          [
            ['todo', 'Task'],
            ['note', 'Note'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={`capture__kind${kind === id ? ' is-active' : ''}`}
            onClick={() => setKind(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {sheet === 'date' && (
        <MobileSheet title="Due date" onClose={() => setSheet(null)}>
          <DatePicker
            value={extras.due_at ?? draft?.parsed.due_at ?? null}
            onPick={(iso) => {
              setExtras((e) => ({ ...e, due_at: iso }));
              setSheet(null);
            }}
            onClose={() => setSheet(null)}
          />
        </MobileSheet>
      )}

      {sheet === 'list' && (
        <MobileSheet title="List" onClose={() => setSheet(null)}>
          <SheetField label="Put it in">
            {lists.map((l) => (
              <button
                key={l.id}
                type="button"
                className={`opt${extras.list_id === l.id ? ' is-on' : ''}`}
                onClick={() => {
                  setExtras((e) => ({ ...e, list_id: l.id }));
                  setSheet(null);
                }}
              >
                <span
                  className="opt__dot"
                  style={colorOf(l.color) ? { background: colorOf(l.color)! } : undefined}
                  aria-hidden="true"
                />
                {l.name}
              </button>
            ))}
          </SheetField>
        </MobileSheet>
      )}

      {sheet === 'tags' && (
        <MobileSheet title="Global tags" onClose={() => setSheet(null)}>
          {catalog.globalTags.length === 0 ? (
            <p className="sheet__prose">
              No global tags yet. Typing <span className="mono">@name</span> as you capture makes
              one.
            </p>
          ) : (
            <SheetField label="Tag it">
              {catalog.globalTags.map((tag) => {
                const on = extras.global_tag_ids?.includes(tag.id) ?? false;
                return (
                  <button
                    key={tag.id}
                    type="button"
                    className={`opt${on ? ' is-on' : ''}`}
                    onClick={() =>
                      setExtras((e) => {
                        const ids = e.global_tag_ids ?? [];
                        return {
                          ...e,
                          global_tag_ids: on ? ids.filter((x) => x !== tag.id) : [...ids, tag.id],
                        };
                      })
                    }
                  >
                    {tag.name}
                  </button>
                );
              })}
            </SheetField>
          )}
        </MobileSheet>
      )}
    </>
  );
}

/* ── Note / task editing ───────────────────────────────────── */

function MobileEditor({
  item,
  catalog,
  onBack,
  onDelete,
}: {
  item: Item;
  catalog: Catalog;
  onBack: () => void;
  onDelete: () => void;
}) {
  const attachments = useAttachments(item.id);
  const [lightbox, setLightbox] = useState<number | null>(null);
  const [props, setProps] = useState(false);
  const set = (patch: Partial<Item>) => void updateItem(item.id, patch);
  // The title is written on every keystroke and handed back a beat later; hold
  // what was typed until the database catches up with it.
  const [title, typedTitle] = useTypedText(item.title, item.id);

  // Read-only here. Mobile reviews and captures; organising is desktop work.
  const list = catalog.lists.find((l) => l.id === item.list_id);
  const anchor = calendarAnchor(item);
  const filed = anchor ? formatDayMonth(anchor) : null;
  const tagNames = [
    ...item.list_tag_ids.map((id) => catalog.listTags.find((t) => t.id === id)?.name),
    ...item.global_tag_ids.map((id) => catalog.globalTags.find((t) => t.id === id)?.name),
  ].filter((name): name is string => Boolean(name));

  return (
    <>
      <header className="mhead mhead--editor">
        <button type="button" className="mhead__back" onClick={onBack}>
          ‹ Back
        </button>
        <div className="spacer" />
        {item.kind === 'todo' && (
          // The one priority control mobile gets: urgent or not.
          <button
            type="button"
            className={`mhead__icon${item.priority === 1 ? ' is-active' : ''}`}
            aria-label="Toggle urgent"
            onClick={() => set({ priority: item.priority === 1 ? 4 : 1 })}
          >
            <span className="pri-dot pri-dot--1" />
          </button>
        )}
        <button type="button" className="mhead__icon" aria-label="Delete" onClick={onDelete}>
          ⌫
        </button>
      </header>

      <div className="mscroll mscroll--editor">
        <div className="editor__titleRow">
          {item.kind === 'todo' && (
            <Checkbox kind="todo" size={22} done={item.done} onToggle={() => void toggleDone(item)} />
          )}
          <input
            className="editor__title mtitle"
            value={title}
            placeholder="Untitled"
            aria-label="Title"
            onChange={(e) => {
              typedTitle(e.target.value);
              set({ title: e.target.value });
            }}
          />
        </div>

        {/* The whole chip row is the way in to editing. On a phone a separate
            "properties" button would be one more thing to find; the values you
            want to change are already right here, so tapping them is the least
            surprising gesture available. */}
        <button type="button" className="editor__tags mprops__open" onClick={() => setProps(true)}>
          {filed && <span className="chip chip--date">{filed}</span>}
          <span className="chip">{list?.name ?? 'Inbox'}</span>
          {item.priority < 4 && (
            <span className="chip chip--pri">
              <span className={`pri-dot pri-dot--${item.priority}`} aria-hidden="true" />P
              {item.priority}
            </span>
          )}
          {tagNames.map((tag) => (
            <span className="chip" key={tag}>
              {tag}
            </span>
          ))}
          <span className="mprops__edit">Edit</span>
        </button>

        <div className="editor__body">
          <MarkdownEditor
            docKey={item.id}
            value={item.body}
            onChange={(body) => set({ body })}
            placeholder="Write…"
          />
        </div>
      </div>

      <AttachmentGallery itemId={item.id} attachments={attachments} onOpen={setLightbox} />

      {props && (
        <PropertiesSheet item={item} catalog={catalog} onClose={() => setProps(false)} />
      )}

      {lightbox !== null && attachments.length > 0 && (
        <Lightbox
          attachments={attachments}
          index={Math.min(lightbox, attachments.length - 1)}
          onIndex={setLightbox}
          onClose={() => setLightbox(null)}
        />
      )}
    </>
  );
}

/* ── Properties ────────────────────────────────────── */

const PRIORITIES: Array<{ value: Priority; label: string }> = [
  { value: 1, label: 'Urgent' },
  { value: 2, label: 'High' },
  { value: 3, label: 'Medium' },
  { value: 4, label: 'None' },
];

/**
 * List, priority, tags and dates, editable from the phone.
 *
 * Start dates stay desktop-only: they exist to hide future work from Today,
 * which is planning rather than capture, and the brief cuts them from mobile
 * deliberately. Due and remind both earn their place — due is what the
 * calendar files a note under, and a reminder is for the device you carry.
 */
function PropertiesSheet({
  item,
  catalog,
  onClose,
}: {
  item: Item;
  catalog: Catalog;
  onClose: () => void;
}) {
  const [picking, setPicking] = useState<'due_at' | 'remind_at' | null>(null);
  const set = (patch: Partial<Item>) => void updateItem(item.id, patch);

  // Only the tags belonging to the list this item is actually in — the others
  // have no meaning here, which is the whole point of list tags.
  const listTags = catalog.listTags.filter((t) => t.list_id === item.list_id);

  const toggle = (ids: string[], id: string) =>
    ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];

  if (picking) {
    const value = item[picking];
    return (
      <MobileSheet
        title={picking === 'due_at' ? 'Due date' : 'Remind at'}
        onClose={() => setPicking(null)}
      >
        <DatePicker
          value={value}
          onPick={(iso) => {
            set({ [picking]: iso } as Partial<Item>);
            setPicking(null);
          }}
          onClose={() => setPicking(null)}
        />
      </MobileSheet>
    );
  }

  return (
    <MobileSheet title="Details" onClose={onClose}>
      <SheetField label="List">
        {catalog.lists.map((l) => (
          <button
            key={l.id}
            type="button"
            className={`opt${l.id === item.list_id ? ' is-on' : ''}`}
            // Moving list drops the list tags, because they do not exist
            // outside the list that owns them.
            onClick={() => void moveToList(item.id, l.id)}
          >
            <span
              className="opt__dot"
              style={colorOf(l.color) ? { background: colorOf(l.color)! } : undefined}
              aria-hidden="true"
            />
            {l.name}
          </button>
        ))}
      </SheetField>

      {item.kind === 'todo' && (
        <SheetField label="Priority">
          {PRIORITIES.map((p) => (
            <button
              key={p.value}
              type="button"
              className={`opt${item.priority === p.value ? ' is-on' : ''}`}
              onClick={() => set({ priority: p.value })}
            >
              {p.value < 4 && (
                <span className={`pri-dot pri-dot--${p.value}`} aria-hidden="true" />
              )}
              {p.label}
            </button>
          ))}
        </SheetField>
      )}

      <SheetField label="Dates">
        <button type="button" className="optRow" onClick={() => setPicking('due_at')}>
          <span>Due</span>
          <span className={`optRow__value${item.due_at ? '' : ' is-empty'}`}>
            {item.due_at ? formatDueShort(item.due_at) : 'Not set'}
          </span>
        </button>
        <button type="button" className="optRow" onClick={() => setPicking('remind_at')}>
          <span>Remind</span>
          <span className={`optRow__value${item.remind_at ? '' : ' is-empty'}`}>
            {item.remind_at ? formatDueShort(item.remind_at) : 'Not set'}
          </span>
        </button>
      </SheetField>

      {listTags.length > 0 && (
        <SheetField label={`Tags in ${catalog.lists.find((l) => l.id === item.list_id)?.name ?? 'this list'}`}>
          {listTags.map((tag) => (
            <button
              key={tag.id}
              type="button"
              className={`opt${item.list_tag_ids.includes(tag.id) ? ' is-on' : ''}`}
              onClick={() => set({ list_tag_ids: toggle(item.list_tag_ids, tag.id) })}
            >
              {tag.name}
            </button>
          ))}
        </SheetField>
      )}

      {catalog.globalTags.length > 0 && (
        <SheetField label="Global tags">
          {catalog.globalTags.map((tag) => (
            <button
              key={tag.id}
              type="button"
              className={`opt${item.global_tag_ids.includes(tag.id) ? ' is-on' : ''}`}
              onClick={() => set({ global_tag_ids: toggle(item.global_tag_ids, tag.id) })}
            >
              {tag.name}
            </button>
          ))}
        </SheetField>
      )}
    </MobileSheet>
  );
}
